import { ChangeDetectionStrategy, Component, ElementRef, HostListener, computed, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { DecimalPipe, NgStyle } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { RadioBoxComponent, RadioIconDirective, RadioOption } from '@libs/front/components/radiobox/radiobox.component';
import { DataTableComponent, TableCellDirective, TableColumn } from '@libs/front/components/data-table/data-table.component';
import { ButtonComponent } from '@libs/front/components/button/button.component';
import { CheckboxComponent } from '@libs/front/components/checkbox/checkbox.component';
import { CartePopupComponent } from '@libs/front/components/popup-panel/popup-panel.component';
import { ScrollableComponent } from 'src/app/shared/components/scrollable/scrollable.component';
import { Hole, HoleLoad, HoleStatus, HoleStatusHistoryUtil } from 'src/app/services/hole.service';

import { BlastHolesStateService, HoleStatusFilter } from '../../blast-holes-state.service';
import { HolesLegendPanelComponent } from '../../shared/holes-legend-panel/holes-legend-panel.component';
import { ProgressChipComponent } from '../../shared/progress-chip/progress-chip.component';
import { readColumnKeys, storeColumnKeys } from '../../shared/column-preferences';
import {
  ALL_HOLES_ICON,
  DRILLED_STATUSES,
  HOLE_STATUS_ICONS,
  LOADED_STATUSES,
  STATE_LEGEND_STATUSES,
  countByStatus,
  holeProgress,
} from '../../shared/hole-status.utils';

interface LegendItem extends RadioOption {
  icon: string;
}

/** One table row; keys match the column keys. */
interface HoleRow {
  publicGuid:       string;
  name:             string;
  status:           string;
  head_XY:          string | null;
  toe_XY:           string | null;
  lengthPlanned:    string | null;
  lengthDrilled:    string | null;
  minedDrilled:     string | null;
  toeLevelPlanned:  string | null;
  toeLevelMesured:  string | null;
  diameterPlanned:  string | null;
  diameterMeasured: string | null;
  hasSurveyed:      boolean;
  hasWater:         boolean;
  hasTube:          boolean;
  comments:         string;
  // Loading quantities (from the blast loading data, fetched on demand)
  bulkPlanned:        string | null;
  bulkMeasured:       string | null;
  stemmingPlanned:    string | null;
  stemmingMeasured:   string | null;
  linearPlanned:      string | null;
  linearMeasured:     string | null;
  cartridgesPlanned:  string | null;
  cartridgesMeasured: string | null;
  loadingDate:        string | null;
}

const COLUMNS: readonly TableColumn[] = [
  { key: 'name',             label: 'QAQC_TAB.HOLE' },
  { key: 'status',           label: 'QAQC_TAB.STATUS' },
  { key: 'head_XY',          label: 'QAQC_TAB.HEAD_XY' },
  { key: 'toe_XY',           label: 'QAQC_TAB.TOE_XY' },
  { key: 'lengthPlanned',    label: 'QAQC_TAB.LENGTH_PLANNED' },
  { key: 'lengthDrilled',    label: 'QAQC_TAB.LENGTH_DRILLED' },
  { key: 'minedDrilled',     label: 'QAQC_TAB.MINED_DRILLED' },
  { key: 'toeLevelPlanned',  label: 'QAQC_TAB.TOE_LEVEL_PLANNED' },
  { key: 'toeLevelMesured',  label: 'QAQC_TAB.TOE_LEVEL_MEASURED' },
  { key: 'diameterPlanned',  label: 'QAQC_TAB.DIAMETER_PLANNED' },
  { key: 'diameterMeasured', label: 'QAQC_TAB.DIAMETER_MEASURED' },
  { key: 'hasSurveyed',      label: 'QAQC_TAB.HAS_SURVEYED' },
  { key: 'hasWater',         label: 'QAQC_TAB.WATER' },
  { key: 'hasTube',          label: 'QAQC_TAB.TUBE' },
  { key: 'comments',         label: 'QAQC_TAB.COMMENT' },
  { key: 'bulkPlanned',        label: 'HOLES_TAB.QUALITY_COL_BULK_PLANNED' },
  { key: 'bulkMeasured',       label: 'HOLES_TAB.QUALITY_COL_BULK_MEASURED' },
  { key: 'stemmingPlanned',    label: 'HOLES_TAB.QUALITY_COL_STEMMING_PLANNED' },
  { key: 'stemmingMeasured',   label: 'HOLES_TAB.QUALITY_COL_STEMMING_MEASURED' },
  { key: 'linearPlanned',      label: 'HOLES_TAB.STATE_COL_LINEAR_PLANNED' },
  { key: 'linearMeasured',     label: 'HOLES_TAB.STATE_COL_LINEAR_MEASURED' },
  { key: 'cartridgesPlanned',  label: 'HOLES_TAB.STATE_COL_CARTRIDGES_PLANNED' },
  { key: 'cartridgesMeasured', label: 'HOLES_TAB.STATE_COL_CARTRIDGES_MEASURED' },
  { key: 'loadingDate',        label: 'HOLES_TAB.STATE_COL_LOADING_DATE' },
];

/** Columns shown by default (the Figma table); the picker can add the others. */
const DEFAULT_COLUMN_KEYS = ['name', 'status', 'lengthPlanned', 'lengthDrilled', 'toeLevelMesured'];

/** Columns fed by the blast loading data (fetching it is triggered when one of them is visible). */
const LOADING_COLUMN_KEYS: ReadonlySet<string> = new Set([
  'bulkPlanned', 'bulkMeasured',
  'stemmingPlanned', 'stemmingMeasured',
  'linearPlanned', 'linearMeasured',
  'cartridgesPlanned', 'cartridgesMeasured',
  'loadingDate',
]);

/**
 * Holes › State view: status legend (map filter), drilled / loaded progress and the hole table.
 * Design source : Figma 2544:670245
 */
@Component({
  selector:        'app-holes-state-view',
  standalone:      true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers:       [DecimalPipe],
  imports: [
    FormsModule,
    NgStyle,
    TranslateModule,
    RadioBoxComponent,
    RadioIconDirective,
    DataTableComponent,
    TableCellDirective,
    ButtonComponent,
    CheckboxComponent,
    CartePopupComponent,
    ScrollableComponent,
    HolesLegendPanelComponent,
    ProgressChipComponent,
  ],
  templateUrl: './holes-state-view.component.html',
  styleUrls:   ['./holes-state-view.component.scss'],
})
export class HolesStateViewComponent {
  protected readonly state = inject(BlastHolesStateService);
  private readonly translate = inject(TranslateService);
  private readonly decimal   = inject(DecimalPipe);

  private readonly lang = toSignal(
    this.translate.onLangChange.pipe(map(event => event.lang)),
    { initialValue: this.translate.currentLang },
  );

  // ── Legend ────────────────────────────────────────────────────────────────

  protected readonly legendItems = computed<LegendItem[]>(() => {
    this.lang();
    const holes  = this.state.holes();
    const total  = holes.length;
    const counts = countByStatus(holes);

    const items: LegendItem[] = [{
      value: 'actual',
      label: this.translate.instant('HOLES_TAB.LEGEND_ACTUAL'),
      icon:  ALL_HOLES_ICON,
    }];

    for (const status of STATE_LEGEND_STATUSES) {
      const count = counts.get(status) ?? 0;
      if (status === HoleStatus.BLASTED && count === 0) continue;
      const percent = total ? Math.round((count / total) * 100) : 0;
      items.push({
        value: status,
        label: `${this.translate.instant('HOLE.' + status)} - ${percent}% - ${count}/${total}`,
        icon:  HOLE_STATUS_ICONS[status],
      });
    }
    return items;
  });

  protected onFilterChange(value: string): void {
    this.state.statusFilter.set(value as HoleStatusFilter);
  }

  // ── Progress chips ────────────────────────────────────────────────────────

  protected readonly drilled = computed(() => holeProgress(this.state.holes(), DRILLED_STATUSES));
  protected readonly loaded  = computed(() => holeProgress(this.state.holes(), LOADED_STATUSES));

  protected retryLoading(): void {
    void this.state.loadLoading(true);
  }

  // ── Table ─────────────────────────────────────────────────────────────────

  protected readonly allColumns = COLUMNS as TableColumn[];
  /** User's column choice, remembered in the browser (see shared/column-preferences.ts) */
  protected readonly visibleColumnKeys = signal<Set<string>>(readColumnKeys('state', COLUMNS, DEFAULT_COLUMN_KEYS));
  protected readonly visibleColumns = computed(() =>
    this.allColumns.filter(column => this.visibleColumnKeys().has(column.key)),
  );

  protected onColumnsChange(keys: Set<string>): void {
    this.visibleColumnKeys.set(keys);
    storeColumnKeys('state', keys);
  }

  protected readonly columnsOpen    = signal(false);
  protected readonly columnsPopupPos = signal({ top: '0px', right: '0px' });
  private readonly columnsBtnRef     = viewChild<ElementRef<HTMLElement>>('columnsBtn');

  protected readonly allColumnsVisible = computed(() => this.visibleColumnKeys().size === this.allColumns.length);

  /** Closes the column picker on any click outside its button / panel. */
  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.columnsOpen()) return;
    const wrapper = this.columnsBtnRef()?.nativeElement;
    if (wrapper && !wrapper.contains(event.target as Node)) this.columnsOpen.set(false);
  }

  /** Toggles the column picker, anchored under the right edge of its button. */
  protected openColumns(el: HTMLElement): void {
    const rect = el.getBoundingClientRect();
    this.columnsPopupPos.set({
      top:   `${rect.bottom + 4}px`,
      right: `${window.innerWidth - rect.right}px`,
    });
    this.columnsOpen.set(!this.columnsOpen());
  }

  /** The last visible column cannot be hidden (the table always keeps one column). */
  protected isLastVisibleColumn(key: string): boolean {
    const keys = this.visibleColumnKeys();
    return keys.size === 1 && keys.has(key);
  }

  protected toggleColumn(key: string): void {
    const keys = new Set(this.visibleColumnKeys());
    if (keys.has(key)) {
      if (keys.size === 1) return;
      keys.delete(key);
    } else {
      keys.add(key);
    }
    this.onColumnsChange(keys);
  }

  /** Shows every column, or goes back to the default columns when all are already shown. */
  protected toggleAllColumns(): void {
    const keys = this.allColumnsVisible()
      ? new Set(DEFAULT_COLUMN_KEYS)
      : new Set(this.allColumns.map(column => column.key));
    this.onColumnsChange(keys);
  }

  protected readonly rows = computed<HoleRow[]>(() =>
    this.state.holes().map(hole => this.toRow(hole, this.state.loadFor(hole))),
  );

  constructor() {
    // Loading quantities are fetched once a quantity column is visible (same rule as the Quality view).
    effect(() => {
      const needed = this.visibleColumns().some(column => LOADING_COLUMN_KEYS.has(column.key));
      if (this.state.blastId() && needed && !this.state.loading()) untracked(() => void this.state.loadLoading());
    });
  }

  protected onRowClick(row: HoleRow): void {
    this.state.toggleHole(row.publicGuid);
  }

  private toRow(hole: Hole, load: HoleLoad | undefined): HoleRow {
    const geometry = hole.Geometry;
    const lengthDrilled = geometry?.drilllength ?? geometry?.Measure?.length;
    const toeLevel = hole.calculatedValues?.toeLevelData;
    const measuredLength = geometry?.Measure?.length;

    return {
      publicGuid:       hole.publicGuid,
      name:             hole.name,
      status:           this.statusHistoryLabel(hole),
      head_XY:          this.format(hole.calculatedValues?.headPositionMagnitude),
      toe_XY:           this.format(hole.calculatedValues?.toePositionMagnitude),
      lengthPlanned:    this.format(geometry?.Design?.length),
      lengthDrilled:    this.format(lengthDrilled),
      minedDrilled:     measuredLength ? this.format(measuredLength) : null,
      toeLevelPlanned:  this.format(toeLevel?.planned),
      toeLevelMesured:  this.format(toeLevel?.measured),
      diameterPlanned:  this.format(geometry?.Design?.diameter != null ? geometry.Design.diameter * 1000 : null),
      diameterMeasured: geometry?.Measure?.diameter ? this.format(geometry.Measure.diameter * 1000) : null,
      hasSurveyed:      !!geometry?.surveyed,
      hasWater:         !!hole.hasWater,
      hasTube:          !!hole.hasTube,
      comments:         (hole.comments ?? []).join(', '),
      bulkPlanned:        this.format(load?.plannedTotalBulk),
      bulkMeasured:       this.format(load?.measuredTotalBulk),
      stemmingPlanned:    this.format(load?.plannedSteaming),
      stemmingMeasured:   this.format(load?.measuredSteaming),
      linearPlanned:      this.format(load?.plannedLinearQt),
      linearMeasured:     this.format(load?.measuredLinearQt),
      cartridgesPlanned:  this.format(load?.plannedCartridges, '1.0-0'),
      cartridgesMeasured: this.format(load?.measuredCartridges, '1.0-0'),
      loadingDate:        load?.loadingDate ?? null,
    };
  }

  private statusHistoryLabel(hole: Hole): string {
    const statuses = hole.statusHistory !== undefined && hole.statusHistory !== null
      ? HoleStatusHistoryUtil.getStatusHistory(hole.statusHistory)
      : [hole.Status as HoleStatus];
    return statuses.map(status => this.translate.instant('HOLE.' + status)).join(', ');
  }

  private format(value: number | null | undefined, digits = '1.2-2'): string | null {
    return value == null || Number.isNaN(value) ? null : this.decimal.transform(value, digits);
  }
}
